import { useMemo, useState } from 'react';
import { X } from 'lucide-react';
import { formatCurrency, formatDuration, formatTime } from '../../../utils/datetime';

const PAYMENT_METHODS = [
  { id: 'mobile_money', label: 'Mobile Money' },
  { id: 'card', label: 'Card' },
  { id: 'cash', label: 'Cash' },
];

const CheckoutSheet = ({ appointment, currency: businessCurrency, onClose, onComplete, submitting }) => {
  const appt = appointment;
  const currency = appt.currency || appt.service?.currency || businessCurrency;
  const price = Number(appt.price) || Number(appt.service?.price) || 0;
  const deposit = appt.depositPaid ? Number(appt.depositAmount) || 0 : 0;
  const balance = Math.max(0, Number(appt.amountDue) || price - deposit);

  const [method, setMethod] = useState('mobile_money');

  const lines = useMemo(
    () => [
      { label: 'Service', value: formatCurrency(price, currency) },
      ...(deposit > 0
        ? [{ label: 'Deposit already deducted', value: `−${formatCurrency(deposit, currency)}` }]
        : []),
      { label: 'Amount due', value: formatCurrency(balance, currency), strong: true },
    ],
    [balance, currency, deposit, price]
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink-950/50 p-0 sm:items-center sm:p-4"
      onClick={onClose}
      role="presentation"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="checkout-sheet-title"
        onClick={(event) => event.stopPropagation()}
        className="max-h-[92vh] w-full max-w-md overflow-y-auto bg-sand-50 shadow-[0_30px_60px_-30px_rgba(6,18,15,0.55)] ring-1 ring-ink-900/10"
      >
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-ink-900/10 bg-sand-50 px-5 py-4">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.22em] text-saffron-600">Checkout</p>
            <h2
              id="checkout-sheet-title"
              className="mt-1 font-display text-2xl font-light text-ink-900"
            >
              {appt.client?.name || 'Client'}
            </h2>
            <p className="mt-1 text-sm text-ink-700/75">
              {appt.service?.name || 'Service'}
              {appt.service?.durationMinutes
                ? ` · ${formatDuration(appt.service.durationMinutes)}`
                : ''}
              {appt.staff?.name ? ` · ${appt.staff.name}` : ''}
              {' · '}
              {formatTime(appt.startAt)}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="flex h-9 w-9 flex-none items-center justify-center text-ink-700 transition-colors hover:bg-ink-900/5"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-6 px-5 py-5">
          <ul className="divide-y divide-ink-900/10 bg-white ring-1 ring-ink-900/10">
            {lines.map((line) => (
              <li
                key={line.label}
                className="flex items-center justify-between gap-4 px-4 py-3 text-sm"
              >
                <span className={line.strong ? 'font-medium text-ink-900' : 'text-ink-700/80'}>
                  {line.label}
                </span>
                <span className={line.strong ? 'font-semibold text-ink-900' : 'text-ink-900'}>
                  {line.value}
                </span>
              </li>
            ))}
          </ul>

          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-[0.14em] text-ink-700">
              Payment method
            </p>
            <div className="flex flex-wrap gap-2">
              {PAYMENT_METHODS.map((option) => (
                <button
                  key={option.id}
                  type="button"
                  onClick={() => setMethod(option.id)}
                  className={`px-4 py-2.5 text-sm ${
                    method === option.id
                      ? 'bg-ink-900 text-sand-50'
                      : 'bg-white text-ink-800 ring-1 ring-ink-900/10 hover:bg-ink-900/5'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>

          <button
            type="button"
            disabled={submitting}
            onClick={() =>
              onComplete?.({
                paymentMethod: method,
                amountPaid: balance,
                amountDue: 0,
              })
            }
            className="w-full bg-saffron-500 px-6 py-4 text-sm font-semibold uppercase tracking-[0.12em] text-ink-900 transition-colors hover:bg-saffron-400 disabled:opacity-50"
          >
            {submitting ? 'Completing…' : 'Complete payment'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSheet;
